import api from '../../api/api';
import { isAxiosError } from '../../api/errorHandling';
import type {
	ColumnDefinitionRequest,
	CreateTableRequest,
	DynamicTable,
} from './dynamicTables.types';

// axios baseURL already ends with `/api`, so paths start at /DynamicTables.
const BASE = '/DynamicTables/v1/tables';

// Backend flags an unprovisioned database with a 400 whose ProblemDetails title
// begins with this text — treated as an environment warning, not a user error.
const NOT_PROVISIONED_PREFIX =
	'Dynamic tables are not provisioned for this database';

// Extracts the ProblemDetails `title` from a 400 (or any axios error) response.
export const problemTitle = (error: unknown): string | null => {
	if (isAxiosError(error)) {
		const data = error.response?.data as { title?: string } | undefined;
		if (data?.title) return data.title;
	}
	return null;
};

// True when the error is the "database not provisioned" environment problem.
export const isNotProvisioned = (error: unknown): boolean => {
	const title = problemTitle(error);
	return !!title && title.startsWith(NOT_PROVISIONED_PREFIX);
};

// 1. GET /tables -> 200 array | 204 none.
export const listTables = async (): Promise<DynamicTable[]> => {
	const res = await api.get(BASE);
	if (res.status === 204 || !res.data) return [];
	return res.data as DynamicTable[];
};

// 2. GET /tables/{name} -> 200 single | 204 (not found).
export const getTable = async (name: string): Promise<DynamicTable | null> => {
	const res = await api.get(`${BASE}/${encodeURIComponent(name)}`);
	if (res.status === 204 || !res.data) return null;
	return res.data as DynamicTable;
};

// 3. POST /tables/create -> 200 created table.
export const createTable = async (
	body: CreateTableRequest,
): Promise<DynamicTable> => {
	const res = await api.post(`${BASE}/create`, body);
	return res.data as DynamicTable;
};

// 4. DELETE /tables/delete/{name} -> 204. Drops the SQL table and all rows.
export const deleteTable = async (name: string): Promise<void> => {
	await api.del(`${BASE}/delete/${encodeURIComponent(name)}`);
};

// 5. POST /tables/{name}/columns/add -> 200 refreshed table definition.
export const addColumn = async (
	name: string,
	body: ColumnDefinitionRequest,
): Promise<DynamicTable> => {
	const res = await api.post(
		`${BASE}/${encodeURIComponent(name)}/columns/add`,
		body,
	);
	return res.data as DynamicTable;
};

// 6. DELETE /tables/{name}/columns/delete/{colName} -> 200 refreshed definition.
// Drops the column and its data.
export const deleteColumn = async (
	name: string,
	colName: string,
): Promise<DynamicTable> => {
	const res = await api.del(
		`${BASE}/${encodeURIComponent(name)}/columns/delete/${encodeURIComponent(
			colName,
		)}`,
	);
	return res.data as DynamicTable;
};
